import React, { ReactNode } from 'react';
import { isFragment } from 'react-is';

/**
 * When adding a new subcomponent, make sure to expose it on `Dag` in `Dag.tsx`.
 */
const subcomponentDisplayNames = [
  'LensLegend',
  'LensSwitcher',
  'ZoomControls',
] as const;
type SubcomponentDisplayName = (typeof subcomponentDisplayNames)[number];

type ReactSubcomponent = React.ReactNode & {
  type: { displayName: SubcomponentDisplayName };
};

export type Subcomponent<TProps extends Record<string, any>> = React.FC<TProps> & {
  displayName: SubcomponentDisplayName;
};

type SubcomponentMap = {
  otherChildren: React.ReactNode[];
  subcomponents: {
    [key in SubcomponentDisplayName]?: React.ReactNode;
  };
};

export function getSubcomponents(
  children: React.ReactNode | undefined,
): SubcomponentMap {
  if (!children) return { otherChildren: [], subcomponents: {} };
  const result: SubcomponentMap = { otherChildren: [], subcomponents: {} };

  React.Children.forEach(children, (child: ReactNode) => {
    if (isFragment(child)) {
      const element = child as React.ReactElement<{ children?: React.ReactNode }>;
      const recursiveSubcompoents = getSubcomponents(element.props?.children);
      result.otherChildren.push(...recursiveSubcompoents.otherChildren);
      result.subcomponents = {
        ...result.subcomponents,
        ...recursiveSubcompoents.subcomponents,
      };
      return;
    }
    const displayName = (child as ReactSubcomponent | null)?.type?.displayName
      // Becomes `LensLegend` from `Dag.LensLegend`
      .split('.')
      .pop() as SubcomponentDisplayName;
    if (!displayName || !subcomponentDisplayNames.includes(displayName)) {
      result.otherChildren.push(child);
      return;
    }
    result.subcomponents[displayName] = child;
  });
  return result;
}
